<template>
  <div class="flex flex-col gap-y-4 text-left" data-testid="send-record-step-2">
    <div>
      <p class="text-sm font-bold text-textPrimary mb-1">Plantão de origem</p>
      <!-- A vaga de origem entra selecionada e TRAVADA: é de onde o modal foi aberto. -->
      <label
        class="flex items-start gap-2 rounded-lg border border-primary bg-primary5/30 px-3 py-2"
        :data-testid="`send-record-station-row-${originStationId}`"
      >
        <input
          type="checkbox"
          class="mt-1"
          checked
          disabled
          :data-testid="`send-record-station-checkbox-${originStationId}`"
        />
        <span class="flex-1">
          <span class="block text-sm text-textPrimary">{{ describe(origin) }}</span>
          <span class="block text-xs text-neutral50">{{ subtitle(origin) }}</span>
        </span>
        <span :class="chipClass(origin)">{{ statusLabel(origin) }}</span>
      </label>
    </div>

    <div>
      <p class="text-sm font-bold text-textPrimary mb-1">Adicionar outros plantões</p>

      <!-- BF16: a lista vem da rota de candidatos (mesmo médico, até 30 dias). -->
      <p
        v-if="error"
        class="flex flex-col items-start gap-1 text-sm text-negative"
        data-testid="send-record-stations-error"
      >
        <span>{{ error }}</span>
        <button
          type="button"
          class="font-semibold underline"
          data-testid="send-record-stations-retry"
          @click="emit('retry')"
        >
          Tentar novamente
        </button>
      </p>

      <p
        v-else-if="loading"
        class="text-xs text-neutral50"
        data-testid="send-record-stations-loading"
      >
        Carregando outros plantões…
      </p>

      <p
        v-else-if="!otherStations.length"
        class="text-xs text-neutral50"
        data-testid="send-record-stations-empty"
      >
        Nenhum outro plantão do médico nos últimos 30 dias.
      </p>

      <div v-else class="flex flex-col gap-1">
        <label
          v-for="option in otherStations"
          :key="option.doctorStationId"
          class="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2"
          :data-testid="`send-record-station-row-${option.doctorStationId}`"
        >
          <input
            type="checkbox"
            class="mt-1"
            :checked="selected.includes(option.doctorStationId)"
            :data-testid="`send-record-station-checkbox-${option.doctorStationId}`"
            @change="toggle(option.doctorStationId, $event)"
          />
          <span class="flex-1">
            <span class="block text-sm text-textPrimary">{{ describe(option) }}</span>
            <span class="block text-xs text-neutral50">{{ subtitle(option) }}</span>
          </span>
          <span class="flex flex-col items-end gap-1">
            <span :class="chipClass(option)">{{ statusLabel(option) }}</span>
            <span
              v-if="option.hasOpenActivityRecord"
              class="text-xs font-bold rounded-full px-2 py-0.5 h-fit whitespace-nowrap bg-orange-100 text-orange-800"
              :data-testid="`send-record-station-open-badge-${option.doctorStationId}`"
            >
              Registro em aberto
            </span>
          </span>
        </label>
      </div>
    </div>

    <!--
      Balão ⑩: plantões "Registro OK" continuam marcáveis (às vezes a folha está errada), mas
      com o aviso à vista. É aviso, nunca bloqueio — quem decide é a auditoria.
    -->
    <p
      v-if="alreadyOkSelected.length"
      class="rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-900"
      data-testid="send-record-already-ok-warning"
    >
      {{ alreadyOkSelected.length === 1 ? 'Este plantão já tem' : 'Estes plantões já têm' }} as duas
      pernas por fonte de ranking maior; o registro será aceito mas pode não mudar o horário
      efetivo.
    </p>

    <p class="text-xs text-neutral50" data-testid="send-record-stations-summary">
      {{ selected.length }}
      {{ selected.length === 1 ? 'plantão selecionado gera' : 'plantões selecionados geram' }}
      {{ selected.length }}
      {{ selected.length === 1 ? 'registro' : 'registros' }}, com os mesmos anexos compartilhados.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  EffectiveRegistrationStatus,
  EFFECTIVE_REGISTRATION_STATUS_LABELS
} from '@/constants/ds-attendance.constants'
import { DEFAULT_UTC_OFFSET, formatIsoDateOnlyBR } from '@/utils/date'
import { isoToTimeInput, type SendRecordStationOption } from './types'

/**
 * Passo 2 de M2 — multi-plantão (Q15). O modelo continua "1 registro = 1
 * plantão": o multi-seleção é um atalho de UI que cria N registros irmãos com o
 * MESMO pacote de anexos.
 *
 * BF16 — `otherStations` some da lista já pronta e passa a vir da rota
 * `GET .../activity-records/candidates` (sempre do MESMO médico da origem);
 * as antigas abas "Deste médico"/"Desta escala" saem junto — sem outro médico
 * no contrato, a segunda aba nunca teria o que mostrar.
 */

const props = defineProps<{
  origin: SendRecordStationOption
  otherStations: SendRecordStationOption[]
  selected: number[]
  loading: boolean
  error: string | null
}>()

const emit = defineEmits<{ 'update:selected': [value: number[]]; retry: [] }>()

const originStationId = computed(() => props.origin.doctorStationId)

const alreadyOkSelected = computed(() =>
  props.otherStations
    .concat(props.origin)
    .filter(
      (option) =>
        props.selected.includes(option.doctorStationId) &&
        option.registrationStatus === EffectiveRegistrationStatus.OK
    )
)

function toggle(doctorStationId: number, event: Event): void {
  const checked = (event.target as HTMLInputElement).checked
  const next = checked
    ? Array.from(new Set([...props.selected, doctorStationId]))
    : props.selected.filter((id) => id !== doctorStationId)
  emit('update:selected', next)
}

/** Horário e data SEMPRE na parede do plantão (`option.timezone`), nunca no fuso do navegador. */
function describe(option: SendRecordStationOption): string {
  const offset = option.timezone ?? DEFAULT_UTC_OFFSET
  const hours =
    option.expectedStart && option.expectedEnd
      ? `${isoToTimeInput(option.expectedStart, offset)}–${isoToTimeInput(option.expectedEnd, offset)}`
      : ''
  return [formatDate(option.referenceDate ?? option.expectedStart), hours, option.shiftName]
    .filter(Boolean)
    .join(' · ')
}

function subtitle(option: SendRecordStationOption): string {
  return [option.doctorName, option.healthUnitName, option.scheduleName].filter(Boolean).join(' · ')
}

function statusLabel(option: SendRecordStationOption): string {
  return EFFECTIVE_REGISTRATION_STATUS_LABELS[
    option.registrationStatus ?? EffectiveRegistrationStatus.NONE
  ]
}

function chipClass(option: SendRecordStationOption): string {
  const base = 'text-xs font-bold rounded-full px-2 py-0.5 h-fit whitespace-nowrap'
  switch (option.registrationStatus) {
    case EffectiveRegistrationStatus.OK:
      return `${base} bg-green-100 text-green-800`
    case EffectiveRegistrationStatus.PARTIAL:
      return `${base} bg-yellow-100 text-yellow-800`
    case EffectiveRegistrationStatus.PENDING:
      return `${base} bg-blue-100 text-blue-800`
    default:
      return `${base} bg-gray-100 text-gray-700`
  }
}

/**
 * `referenceDate`/`expectedStart` podem chegar como `yyyy-MM-dd` (contrato de
 * candidatos, BF16) ou ISO completo — nunca via `new Date()`: o construtor lê o
 * dia como meia-noite UTC e volta um dia num navegador em UTC-3.
 */
function formatDate(value?: string | null): string {
  return value ? formatIsoDateOnlyBR(value) : ''
}
</script>
